'use client';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Button } from '@/components/atoms/Button';
import IconValue from '@/components/atoms/IconValue';
import { getConsent, setConsent } from '@/utils/cookieConsent';
import { getColorScheme } from '@/utils/getColorScheme';

// Nav.jsx's own float-scheme treatment for reference: bg-white / bg-neutral-800,
// optionally blurred, wrapped in a margin + `rounded-[var(--shape-radius)]` pill.
// getColorScheme.js doesn't implement these, so they're defined locally here.
const FLOAT_SCHEME_STYLES = {
    float: {
        backgroundColor: 'bg-white',
        textColor: 'text-neutral-800',
        iconColor: 'text-primary-700',
        linkColor: 'text-primary-700',
        secondaryButton: 'secondary',
        thirdButton: 'outline',
    },
    'float-dark': {
        backgroundColor: 'bg-neutral-800',
        textColor: 'text-white',
        iconColor: 'text-white',
        linkColor: 'text-white',
        secondaryButton: 'ghost',
        thirdButton: 'outlineDark',
    },
    'float-blur': {
        backgroundColor:
            'bg-white bg-opacity-70 bg-blur backdrop-blur-md backdrop-filter',
        textColor: 'text-neutral-800',
        iconColor: 'text-primary-700',
        linkColor: 'text-primary-700',
        secondaryButton: 'secondary',
        thirdButton: 'outline',
    },
    'float-blur-dark': {
        backgroundColor:
            'bg-black bg-opacity-70 bg-blur backdrop-blur-md backdrop-filter',
        textColor: 'text-white',
        iconColor: 'text-white',
        linkColor: 'text-white',
        secondaryButton: 'ghost',
        thirdButton: 'outlineDark',
    },
};

function ConsentToggleRow({ label, description, checked, disabled, onChange }) {
    return (
        <div className="flex items-center justify-between gap-4">
            <span className="text-xs sm:text-sm">
                {label}
                {description && (
                    <span className="ml-2 text-[11px] opacity-60 sm:text-xs">
                        {description}
                    </span>
                )}
            </span>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => onChange?.(!checked)}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${
                    checked ? 'bg-primary-600' : 'bg-black/20'
                } ${
                    disabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer'
                }`}
            >
                <span
                    className={`inline-block h-4 w-4 translate-y-0.5 rounded-full bg-white transition-transform duration-200 ${
                        checked ? 'translate-x-[1.15rem]' : 'translate-x-0.5'
                    }`}
                />
            </button>
        </div>
    );
}

export default function CookieConsentBanner({
    Enabled = false,
    Mode = 'simple',
    Text = 'We use cookies to improve your experience, analyze site traffic, and personalize content. You can accept all cookies or decline non-essential ones.',
    AcceptButtonText = 'Accept All',
    DeclineButtonText = 'Decline',
    ManageButtonText = 'Manage Preferences',
    PolicyLink = '/privacy',
    ColorScheme = 'light',
    Icon = 'fa-solid fa-cookie-bite',
    ShowReopenIcon = false,
    ReopenIconPosition = 'right',
}) {
    // Presence of the editor's `edit` param is enough to gate the preview
    // listener — it's inert on the public site since nothing ever dispatches
    // the event there.
    const searchParams = useSearchParams();
    const isEditorContext = Boolean(searchParams?.get('edit'));

    const [hasNoDecision, setHasNoDecision] = useState(false);
    // True once the visitor has reopened an already-made decision (via the
    // minimized icon) to change it — distinct from `hasNoDecision`, which is
    // only about whether a decision exists at all.
    const [reopened, setReopened] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    // Sticky for the life of the page once any preview has fired, so the
    // minimized icon (and reopening it) stays testable via Preview even when
    // `Enabled` hasn't actually been saved yet.
    const [previewSessionActive, setPreviewSessionActive] = useState(false);
    // The docked Site Settings drawer pushes the rest of the page over by
    // setting `document.body.style.marginRight` — mirror that on this fixed
    // element too, since fixed positioning otherwise ignores it entirely and
    // the banner would sit full-bleed underneath the drawer.
    const [dockedPanelWidth, setDockedPanelWidth] = useState(0);
    // The editor's tablet/mobile preview renders the actual page inside a
    // resizable iframe (`.preview-resizable` in EditablePage.jsx) — this
    // banner lives outside that iframe entirely (it's rendered from the root
    // layout, not the per-page component tree), so without this it would
    // ignore the simulated device width and span the full outer window
    // instead of matching the framed preview.
    const [previewFrameRect, setPreviewFrameRect] = useState(null);
    // While previewing, the settings panel sends its current (possibly
    // unsaved) draft values so Preview always reflects what's on screen,
    // not what's last been saved to disk.
    const [previewOverrides, setPreviewOverrides] = useState(null);
    const [managing, setManaging] = useState(false);
    const [analytics, setAnalytics] = useState(true);
    const [marketing, setMarketing] = useState(true);

    useEffect(() => {
        const existing = getConsent();
        setHasNoDecision(!existing);
        if (existing) {
            setAnalytics(existing.analytics);
            setMarketing(existing.marketing);
        }
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const body = document.body;

        const syncPanelWidth = () => {
            setDockedPanelWidth(parseFloat(body.style.marginRight || '0') || 0);
        };

        syncPanelWidth();

        const observer = new MutationObserver(syncPanelWidth);
        observer.observe(body, {
            attributes: true,
            attributeFilter: ['style'],
        });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isEditorContext || typeof document === 'undefined') {
            return undefined;
        }

        const syncPreviewFrameRect = () => {
            const el = document.querySelector('.preview-resizable');
            if (!el) {
                setPreviewFrameRect((prev) => (prev === null ? prev : null));
                return;
            }
            const rect = el.getBoundingClientRect();
            setPreviewFrameRect((prev) =>
                prev && prev.left === rect.left && prev.width === rect.width
                    ? prev
                    : { left: rect.left, width: rect.width }
            );
        };

        syncPreviewFrameRect();
        // The resizable box only changes on drag/breakpoint clicks, but
        // neither dispatches a DOM event we can listen for, so poll — same
        // approach GlobalEditor.jsx already uses for breakpoint detection.
        const intervalId = window.setInterval(syncPreviewFrameRect, 150);
        window.addEventListener('resize', syncPreviewFrameRect);
        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('resize', syncPreviewFrameRect);
        };
    }, [isEditorContext]);

    useEffect(() => {
        if (!isEditorContext || typeof window === 'undefined') {
            return undefined;
        }

        const previewCookieConsent = (event) => {
            setManaging(false);
            setReopened(false);
            setPreviewOverrides(event?.detail || null);
            setPreviewOpen(true);
            setPreviewSessionActive(true);
        };

        window.addEventListener(
            'editor:preview-cookie-consent',
            previewCookieConsent
        );
        return () =>
            window.removeEventListener(
                'editor:preview-cookie-consent',
                previewCookieConsent
            );
    }, [isEditorContext]);

    const showReopenIconSetting =
        previewOverrides?.showReopenIcon ?? ShowReopenIcon;
    const reopenIconPosition =
        previewOverrides?.reopenIconPosition ?? ReopenIconPosition;
    // Once a Preview has fired this page load, treat the feature as "on" for
    // gating purposes even if it hasn't been saved yet, so the whole
    // minimize → reopen flow stays testable from the settings panel.
    const effectivelyEnabled = Enabled || previewSessionActive;

    const showFullBanner =
        previewOpen || (effectivelyEnabled && (hasNoDecision || reopened));
    const showReopenIconButton =
        !showFullBanner &&
        effectivelyEnabled &&
        showReopenIconSetting &&
        !hasNoDecision;

    if (!showFullBanner && !showReopenIconButton) return null;

    const mode = previewOverrides?.mode ?? Mode;
    const text = previewOverrides?.text ?? Text;
    const acceptButtonText =
        previewOverrides?.acceptButtonText ?? AcceptButtonText;
    const declineButtonText =
        previewOverrides?.declineButtonText ?? DeclineButtonText;
    const manageButtonText =
        previewOverrides?.manageButtonText ?? ManageButtonText;
    const policyLink = previewOverrides?.policyLink ?? PolicyLink;
    const colorScheme = previewOverrides?.colorScheme ?? ColorScheme;
    const icon = previewOverrides?.icon ?? Icon;

    const isGranular = mode === 'granular';
    const isFloatScheme = Object.prototype.hasOwnProperty.call(
        FLOAT_SCHEME_STYLES,
        colorScheme
    );

    const {
        backgroundColor,
        textColor,
        iconColor,
        linkColor,
        secondaryButton,
        thirdButton,
        backgroundStyle,
    } = isFloatScheme
        ? FLOAT_SCHEME_STYLES[colorScheme]
        : getColorScheme(colorScheme);
    // Bold, no underline — regardless of whether the scheme's own linkColor
    // happens to bake in font-bold already.
    const linkClassName = `font-bold ${String(linkColor || '')
        .replace(/\bfont-bold\b/g, '')
        .trim()}`;

    const closePreview = () => {
        setPreviewOpen(false);
        // Deliberately keep `previewOverrides` around (rather than nulling
        // it) so the minimized icon and any later reopen still reflect the
        // last-previewed draft values, not the saved ones.
        if (isEditorContext && typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent('editor:preview-cookie-consent-closed')
            );
        }
    };

    const acceptAll = () => {
        setConsent({ analytics: true, marketing: true });
        setHasNoDecision(false);
        setReopened(false);
        closePreview();
    };

    const declineAll = () => {
        setConsent({ analytics: false, marketing: false });
        setHasNoDecision(false);
        setReopened(false);
        closePreview();
    };

    const savePreferences = () => {
        setConsent({ analytics, marketing });
        setHasNoDecision(false);
        setReopened(false);
        closePreview();
    };

    const reopenBanner = () => {
        const existing = getConsent();
        if (existing) {
            setAnalytics(existing.analytics);
            setMarketing(existing.marketing);
        }
        setManaging(isGranular);
        setReopened(true);
    };

    // Floats get a gap from the viewport edges (bottom + sides) and the
    // site's shape radius; the plain bar sits flush and square-cornered.
    const surfaceClassName = isFloatScheme
        ? `mx-auto max-w-6xl rounded-[var(--shape-radius)] shadow-xl ${backgroundColor}`
        : `w-full shadow-[0_-10px_30px_rgba(0,0,0,0.15)] ${backgroundColor}`;

    const wrapperPositionStyle = previewFrameRect
        ? { left: previewFrameRect.left, width: previewFrameRect.width }
        : { left: 0, right: dockedPanelWidth };

    if (showReopenIconButton) {
        const isLeftPosition = reopenIconPosition === 'left';

        return (
            <div
                className={`fixed bottom-0 z-[1000] flex p-4 transition-all duration-200 ease-out sm:p-6 ${
                    isLeftPosition ? 'justify-start' : 'justify-end'
                }`}
                style={wrapperPositionStyle}
            >
                <button
                    type="button"
                    onClick={reopenBanner}
                    aria-label="Manage cookie preferences"
                    title="Manage cookie preferences"
                    className={`flex h-9 w-9 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 ${backgroundColor}`}
                    style={backgroundStyle}
                >
                    {icon && (
                        <IconValue
                            value={icon}
                            className={`text-sm ${iconColor}`}
                        />
                    )}
                </button>
            </div>
        );
    }

    return (
        <div
            className={`fixed bottom-0 z-[1000] transition-all duration-200 ease-out ${
                isFloatScheme ? 'p-2 sm:p-6' : ''
            }`}
            style={wrapperPositionStyle}
        >
            <div
                className={`${surfaceClassName} p-3 sm:p-6`}
                style={backgroundStyle}
            >
                <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
                    <div
                        className={`flex flex-1 items-center gap-2 sm:gap-3 ${textColor}`}
                    >
                        {icon && (
                            <IconValue
                                value={icon}
                                className={`text-lg sm:text-2xl ${iconColor}`}
                            />
                        )}
                        <div className="text-xs leading-5 sm:text-sm sm:leading-6">
                            <p>
                                {text}{' '}
                                {policyLink && (
                                    <a
                                        href={policyLink}
                                        className={linkClassName}
                                    >
                                        Learn more
                                    </a>
                                )}
                            </p>

                            {isGranular && managing && (
                                <div className="border-current/10 mt-3 flex flex-col gap-2 rounded-xl border p-2 sm:mt-4 sm:gap-3 sm:p-3">
                                    <ConsentToggleRow
                                        label="Necessary"
                                        description="Always active"
                                        checked
                                        disabled
                                    />
                                    <ConsentToggleRow
                                        label="Analytics"
                                        checked={analytics}
                                        onChange={setAnalytics}
                                    />
                                    <ConsentToggleRow
                                        label="Marketing"
                                        checked={marketing}
                                        onChange={setMarketing}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        className="flex w-full flex-wrap items-center justify-end gap-1.5 [--button-sm-font-size:0.75rem]
                            [--button-sm-px:0.625rem] [--button-sm-py:0.4rem] sm:w-auto sm:gap-2
                            sm:[--button-sm-font-size:0.875rem] sm:[--button-sm-px:1rem] sm:[--button-sm-py:0.75rem]"
                    >
                        {isGranular && !managing && (
                            <Button
                                type="button"
                                variant={thirdButton}
                                size="sm"
                                onClick={() => setManaging(true)}
                            >
                                {manageButtonText}
                            </Button>
                        )}

                        {isGranular && managing ? (
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={savePreferences}
                            >
                                Save Preferences
                            </Button>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant={secondaryButton}
                                    size="sm"
                                    onClick={declineAll}
                                >
                                    {declineButtonText}
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    onClick={acceptAll}
                                >
                                    {acceptButtonText}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
